import {View} from 'react-native'

import {type FamilyLinkDirection} from '#/state/queries/family'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {Logo} from '#/view/icons/Logo'
import {Logotype} from '#/view/icons/Logotype'
import {atoms as a} from '#/alf'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {Text} from '#/components/Typography'

export type FamilyProfile = {
  did: string
  handle: string
  displayName?: string
  avatar?: string
}

/**
 * The family invite "cover" — a gradient card with the initiator's avatar,
 * display name, @handle, role chip, and a role-aware headline. Used verbatim
 * on the Family screen preview, the QR share card, the /family/:code landing,
 * and the DM bubble (compact).
 */
export function FamilyInviteCard({
  profile,
  role,
  headline,
  compact = false,
  showLogo = true,
}: {
  profile: FamilyProfile | undefined
  /** The initiator's own role ('child' = learner asking a parent). */
  role: FamilyLinkDirection
  /** Override the default role-aware headline. */
  headline?: string
  compact?: boolean
  showLogo?: boolean
}) {
  const name = profile?.displayName || profile?.handle || profile?.did || ''
  const defaultHeadline =
    role === 'child'
      ? 'asks you to be their parent'
      : 'invited you to join their family'
  const chip = role === 'child' ? 'Learner' : 'Parent'

  return (
    <LinearGradientBackground
      gradient="sky"
      style={[
        a.rounded_lg,
        a.align_center,
        a.overflow_hidden,
        compact ? [a.p_md, a.gap_sm] : [a.p_xl, a.gap_md, a.py_2xl],
      ]}>
      {profile?.avatar ? (
        <UserAvatar
          avatar={profile.avatar}
          size={compact ? 40 : 64}
          type="user"
        />
      ) : (
        <View
          style={[
            {
              width: compact ? 40 : 64,
              height: compact ? 40 : 64,
              borderRadius: (compact ? 40 : 64) / 2,
              backgroundColor: 'rgba(255,255,255,0.35)',
            },
            a.align_center,
            a.justify_center,
          ]}>
          <Text style={[a.text_lg, a.font_bold, {color: 'white'}]}>
            {(name[0] || '?').toUpperCase()}
          </Text>
        </View>
      )}
      <View style={[a.align_center, a.gap_2xs]}>
        <Text
          style={[
            a.font_semi_bold,
            compact ? a.text_md : a.text_2xl,
            a.text_center,
            {color: 'white'},
          ]}
          numberOfLines={1}>
          {name}
        </Text>
        {profile?.handle ? (
          <Text
            style={[
              a.text_md,
              a.text_center,
              {color: 'rgba(255,255,255,0.85)'},
            ]}
            numberOfLines={1}>
            @{profile.handle}
          </Text>
        ) : null}
      </View>
      <View
        style={[
          a.rounded_full,
          {
            backgroundColor: 'rgba(255,255,255,0.25)',
            paddingHorizontal: compact ? 8 : 12,
            paddingVertical: 2,
          },
        ]}>
        <Text
          style={[
            a.text_xs,
            a.font_semi_bold,
            {color: 'white', letterSpacing: 0.5, textTransform: 'uppercase'},
          ]}>
          {chip}
        </Text>
      </View>
      <Text
        style={[
          a.text_md,
          a.text_center,
          a.leading_snug,
          compact ? a.text_sm : undefined,
          {color: 'white'},
        ]}>
        {headline ?? defaultHeadline}
      </Text>
      {showLogo && (
        <View style={[a.flex_row, a.align_center, {gap: 6, marginTop: 4}]}>
          <Logo width={18} fill="white" />
          {/* Wordmark sized so its height (18) matches the logo icon (18). */}
          <Logotype width={82} fill="white" />
        </View>
      )}
    </LinearGradientBackground>
  )
}
